import React, { useState, useEffect } from "react";
import { Drawer, Button, Input, Upload, Space, Tag, Descriptions, message, Spin, Divider } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { incidentService } from "@iso/services";
import { apiEndpoint } from "@iso/lib/helpers/endpoint";
import { authHeader } from "@iso/lib/helpers/authHeader";
import { INVESTIGATION_TYPES, INVESTIGATION_STATUSES } from "@iso/constants/incidentOptions";

const { TextArea } = Input;

const statusColors = {
  in_progress: "processing",
  submitted: "warning",
  more_info_requested: "error",
  approved: "success",
};

const InvestigationPanel = ({ visible, incident, isSafetyAdmin, isInvestigator, onCancel, onUpdated }) => {
  const [investigation, setInvestigation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [content, setContent] = useState("");
  const [evidence, setEvidence] = useState("");
  const [reviewComment, setReviewComment] = useState("");
  const [evidenceFiles, setEvidenceFiles] = useState([]);
  const [evidenceAttachmentIds, setEvidenceAttachmentIds] = useState([]);

  useEffect(() => {
    if (visible && incident) {
      fetchInvestigation();
    }
  }, [visible, incident]);

  const fetchInvestigation = async () => {
    setLoading(true);
    try {
      const data = await incidentService.getInvestigation(incident.id);
      setInvestigation(data);
      if (data) {
        setContent(data.content || "");
        setEvidence(data.evidence || "");
        setReviewComment(data.review_comment || "");
        // Load existing evidence attachments
        if (data.evidence_attachments && Array.isArray(data.evidence_attachments)) {
          setEvidenceFiles(
            data.evidence_attachments.map((att) => ({
              uid: att.id,
              name: att.url ? att.url.split("/").pop() : `Attachment #${att.id}`,
              status: "done",
              url: att.full_path || att.url,
            }))
          );
          setEvidenceAttachmentIds(data.evidence_attachments.map((att) => att.id));
        }
      }
    } catch (err) {
      // Investigation may not exist yet
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitInvestigation = async () => {
    setSubmitting(true);
    try {
      await incidentService.submitInvestigation(incident.id, { content, evidence, evidence_attachment_ids: evidenceAttachmentIds });
      message.success("Investigation submitted successfully.");
      fetchInvestigation();
      onUpdated && onUpdated();
    } catch (err) {
      message.error(err?.response?.data?.message || "Failed to submit investigation.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleApprove = async () => {
    setSubmitting(true);
    try {
      await incidentService.approveInvestigation(incident.id, reviewComment);
      message.success("Investigation approved.");
      fetchInvestigation();
      onUpdated && onUpdated();
    } catch (err) {
      message.error(err?.response?.data?.message || "Failed to approve investigation.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleRequestMoreInfo = async () => {
    setSubmitting(true);
    try {
      await incidentService.requestMoreInfo(incident.id, reviewComment);
      message.success("More information requested from investigator.");
      fetchInvestigation();
      onUpdated && onUpdated();
    } catch (err) {
      message.error(err?.response?.data?.message || "Failed to request more info.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditSave = async () => {
    setSubmitting(true);
    try {
      await incidentService.editInvestigation(incident.id, { content, evidence, review_comment: reviewComment, evidence_attachment_ids: evidenceAttachmentIds });
      message.success("Investigation updated.");
      fetchInvestigation();
      onUpdated && onUpdated();
    } catch (err) {
      message.error(err?.response?.data?.message || "Failed to update investigation.");
    } finally {
      setSubmitting(false);
    }
  };

  const investigationType = INVESTIGATION_TYPES.find((t) => t.value === incident?.investigation_type);
  const statusOption = INVESTIGATION_STATUSES.find((s) => s.value === investigation?.status);

  return (
    <Drawer
      open={visible}
      onClose={onCancel}
      title="Investigation Details"
      width={600}
      destroyOnClose
    >
      <Spin spinning={loading}>
        {!investigation ? (
          <p>No investigation record found for this incident.</p>
        ) : (
          <>
            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label="Investigation Type">
                {investigationType?.label || incident?.investigation_type || "N/A"}
              </Descriptions.Item>
              <Descriptions.Item label="Investigator">
                {investigation.investigator_name || "N/A"}
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color={statusColors[investigation.status] || "default"}>
                  {statusOption?.label || investigation.status}
                </Tag>
              </Descriptions.Item>
              {investigation.submitted_at && (
                <Descriptions.Item label="Submitted At">
                  {investigation.submitted_at}
                </Descriptions.Item>
              )}
              {investigation.reviewed_by_name && (
                <Descriptions.Item label="Reviewed By">
                  {investigation.reviewed_by_name}
                </Descriptions.Item>
              )}
              {investigation.review_comment && (
                <Descriptions.Item label="Review Comment">
                  {investigation.review_comment}
                </Descriptions.Item>
              )}
            </Descriptions>

            <Divider />

            {/* Investigator View: Can edit content and submit */}
            {(isInvestigator || isSafetyAdmin) && (
              <>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ fontWeight: "bold", display: "block", marginBottom: 4 }}>
                    Investigation Content
                  </label>
                  <TextArea
                    rows={6}
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    placeholder="Enter investigation findings..."
                    disabled={investigation.status === "approved"}
                  />
                </div>

                <div style={{ marginBottom: 16 }}>
                  <label style={{ fontWeight: "bold", display: "block", marginBottom: 4 }}>
                    Evidence Notes
                  </label>
                  <TextArea
                    rows={3}
                    value={evidence}
                    onChange={(e) => setEvidence(e.target.value)}
                    placeholder="Enter evidence references or details..."
                    disabled={investigation.status === "approved"}
                  />
                </div>

                <div style={{ marginBottom: 16 }}>
                  <label style={{ fontWeight: "bold", display: "block", marginBottom: 4 }}>
                    Evidence Attachments
                  </label>
                  <Upload
                    action={apiEndpoint.attachments}
                    headers={authHeader(true)}
                    listType="picture"
                    name="Attachment[file]"
                    fileList={evidenceFiles}
                    multiple
                    data={{ type: "investigation", storage: "filesystem" }}
                    disabled={investigation.status === "approved"}
                    onChange={(info) => {
                      if (info.file && info.file.status === "done" && info.file.response?.attachment) {
                        setEvidenceAttachmentIds((prev) => [...prev, info.file.response.attachment.id]);
                      }
                      setEvidenceFiles([...info.fileList]);
                    }}
                    onRemove={(file) => {
                      const id = file.uid;
                      setEvidenceAttachmentIds((prev) => prev.filter((aid) => aid !== id));
                    }}
                  >
                    <Button icon={<UploadOutlined />} disabled={investigation.status === "approved"}>
                      Upload Evidence
                    </Button>
                  </Upload>
                </div>

                {/* Investigator actions */}
                {isInvestigator && ["in_progress", "more_info_requested"].includes(investigation.status) && (
                  <Button
                    type="primary"
                    onClick={handleSubmitInvestigation}
                    loading={submitting}
                    style={{ marginRight: 8, backgroundColor: '#0B2A1B', borderColor: '#0B2A1B' }}
                  >
                    Submit Investigation
                  </Button>
                )}

                {/* Safety Admin actions */}
                {isSafetyAdmin && investigation.status !== "approved" && (
                  <>
                    <Divider />
                    <div style={{ marginBottom: 16 }}>
                      <label style={{ fontWeight: "bold", display: "block", marginBottom: 4 }}>
                        Review Comment
                      </label>
                      <TextArea
                        rows={2}
                        value={reviewComment}
                        onChange={(e) => setReviewComment(e.target.value)}
                        placeholder="Add review comment (optional)..."
                      />
                    </div>
                    <Space>
                      {investigation.status === "submitted" && (
                        <>
                          <Button type="primary" onClick={handleApprove} loading={submitting}
                            style={{ backgroundColor: '#0B2A1B', borderColor: '#0B2A1B' }}>
                            Approve
                          </Button>
                          <Button danger onClick={handleRequestMoreInfo} loading={submitting}>
                            Request More Info
                          </Button>
                        </>
                      )}
                      <Button onClick={handleEditSave} loading={submitting}
                        style={{ borderColor: '#0B2A1B', color: '#0B2A1B' }}>
                        Save Edits
                      </Button>
                    </Space>
                  </>
                )}
              </>
            )}
          </>
        )}
      </Spin>
    </Drawer>
  );
};

export default InvestigationPanel;
